import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { COOKIE_CONFIG, DOMAIN_CONFIG } from "@/integrations/supabase/config";

type AuthView = "login" | "signup" | "password-reset" | "update-password";

// Cookie utility functions for recovery tokens
export const setCookie = (name: string, value: string, maxAge?: number) => {
  const cookieOptions = [
    `${name}=${encodeURIComponent(value)}`,
    // Only set domain in production
    DOMAIN_CONFIG.parentDomain ? `domain=${DOMAIN_CONFIG.parentDomain}` : "",
    `path=/`,
    `max-age=${maxAge || COOKIE_CONFIG.maxAge}`,
    COOKIE_CONFIG.secure ? "secure" : "",
    `samesite=${COOKIE_CONFIG.sameSite}`,
  ]
    .filter(Boolean)
    .join("; ");

  document.cookie = cookieOptions;
};

export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [cookieKey, ...cookieValueParts] = cookie.trim().split("=");
    if (cookieKey === name) {
      const cookieValue = cookieValueParts.join("=");
      try {
        return decodeURIComponent(cookieValue);
      } catch {
        return cookieValue;
      }
    }
  }
  return null;
};

export const deleteCookie = (name: string) => {
  const cookieOptions = [
    `${name}=`,
    // Only set domain in production
    DOMAIN_CONFIG.parentDomain ? `domain=${DOMAIN_CONFIG.parentDomain}` : "",
    `path=/`,
    "max-age=0",
    COOKIE_CONFIG.secure ? "secure" : "",
    `samesite=${COOKIE_CONFIG.sameSite}`,
  ]
    .filter(Boolean)
    .join("; ");

  document.cookie = cookieOptions;
};

export const getSessionFromCookie = (): {
  access_token?: string;
  refresh_token?: string;
} => {
  const sessionData = getCookie(COOKIE_CONFIG.name);
  if (!sessionData) return {};

  try {
    const parsed = JSON.parse(sessionData);
    return {
      access_token: parsed.access_token,
      refresh_token: parsed.refresh_token,
    };
  } catch {
    return {};
  }
};

export const useAuthPage = () => {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<AuthView>("login");
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Process URL parameters for view changes
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    const viewParam = searchParams.get("view");
    const typeParam = searchParams.get("type");

    console.log("URL parameters:", { tabParam, viewParam, typeParam });

    if (viewParam === "reset-password") {
      setView("password-reset");
    } else if (viewParam === "update-password" || typeParam === "recovery") {
      setView("update-password");
      console.log("Setting view to update-password");

      // Store the recovery token if present
      const hash = location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (accessToken) {
          console.log("Recovery token found in URL");
          setRecoveryToken(accessToken);

          // Store tokens in SUPABASE_SESSION cookie
          const sessionData = {
            access_token: accessToken,
            refresh_token: refreshToken || "",
          };
          setCookie(COOKIE_CONFIG.name, JSON.stringify(sessionData));
        }
      }
    } else if (tabParam === "signup") {
      setView("signup");
    } else if (tabParam === "login") {
      setView("login");
    } else {
      setView("login");
    }
  }, [location.search, location.hash]);

  const handlePasswordUpdate = async (password: string) => {
    try {
      setLoading(true);

      // Get the recovery token from cookie
      const sessionFromCookie = getSessionFromCookie();
      const accessToken = recoveryToken || sessionFromCookie.access_token;
      const refreshToken = sessionFromCookie.refresh_token;

      console.log(
        "Attempting password update with token available:",
        !!accessToken
      );

      if (!accessToken) {
        throw new Error(
          "No recovery token found. Please request a new password reset link."
        );
      }

      // Set the session with the recovery tokens
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || "",
      });

      if (sessionError) {
        throw sessionError;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        toast({
          title: "Error updating password",
          description: updateError.message,
          variant: "destructive",
        });
        throw updateError;
      }

      toast({
        title: "Password updated successfully",
        description: "Your password has been updated. Logging you in...",
      });
      navigate("/auth");
    } catch (error: unknown) {
      console.error("Password update error:", error);
      toast({
        title: "Error updating password",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetView = (view: AuthView) => {
    setView(view);
  };

  return {
    loading,
    setLoading,
    view,
    handleSetView,
    handlePasswordUpdate,
    setView,
    setRecoveryToken,
  };
};
